import React, { FC, MouseEvent, KeyboardEvent, ReactNode } from 'react';
import cn from 'classnames';

import { useTheme, EngineTheme, useMergeClasses } from '../../../../../theme';
import { CircularProgress } from '@mui/material';

export interface Props {
	classes?: EngineTheme['Shared']['Switch'];

	checked: boolean;
	onChange?: (e?: MouseEvent<HTMLDivElement> | KeyboardEvent<Element>) => void;

	required?: boolean;
	disabled?: boolean;
	inverted?: boolean;
	loading?: boolean;

	label?: ReactNode;
	labelPosition?: 'left' | 'right';
	dataTestId?: string;
	dataControlType?: string;
}

const Switch: FC<Props> = ({
	classes,
	checked,
	onChange,
	required,
	disabled,
	inverted,
	loading,
	label,
	labelPosition = 'left',
	dataTestId,
	dataControlType
}) => {
	const { Switch: defaultClasses } = useTheme('Shared');
	const css = useMergeClasses(defaultClasses, classes);

	const handleClick = (e: MouseEvent<HTMLDivElement>) => {
		if (!disabled && !loading && onChange) {
			onChange(e);
		}
	};

	const handleKeyDown = (e: KeyboardEvent) => {
		if (!disabled && !loading && (e.key === 'Enter' || e.key === ' ') && onChange) {
			e.preventDefault();

			onChange(e);
		}
	};

	return (
		<div
			className={cn(css.root, {
				[css.checked]: checked,
				[css.disabled]: disabled,
				[css.required]: required,
				[css.inverted]: inverted,
				[css.loading]: loading,
				[css.labelPosition__left]: labelPosition === 'left',
				[css.labelPosition__right]: labelPosition === 'right'
			})}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			role="switch"
			aria-checked={checked}
			aria-disabled={disabled}
			tabIndex={disabled ? -1 : 0}
			data-test-id={dataTestId}
			data-control-type={dataControlType}
		>
			{label && <div className={css.label}>{label}</div>}

			<div className={css.toggle}>
				<span className={css.handle}>
					<span className={css.handle__circular} />
					<CircularProgress className={css.handle__circular_progress} size={14} thickness={6} />
				</span>
			</div>
		</div>
	);
};

export default Switch;
